import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Card,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Button,
  message,
} from "antd";
import {
  ScheduleOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppContext } from "../context/AppContext";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PtpPage = () => {
  const { company } = useContext(AppContext);
  const [ptps, setPtps] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    dateRange: [moment().subtract(30, "days"), moment()],
    status: "all",
  });
  const token = localStorage.getItem("token");

  const statusColors = {
    Pending: "blue",
    Overdue: "red",
    Completed: "green",
  };

  const columns = [
    {
      title: "Borrower",
      render: (_, record) => {
        return (
          <Text>
            {record.loan_id?.borrower?.first_name +
              " " +
              record.loan_id?.borrower?.last_name || "N/A"}
          </Text>
        );
      },
      sorter: (a, b) => {
        const nameA = a.loan_id?.borrower?.first_name?.toLowerCase() || "";
        const nameB = b.loan_id?.borrower?.last_name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Amount Promised (ZMW)",
      dataIndex: "amount_promised",
      render: (amount) => <Text strong>{amount?.toLocaleString()}</Text>,
      sorter: (a, b) => a.amount_promised - b.amount_promised,
    },
    {
      title: "Date Promised",
      dataIndex: "date_promised",
      render: (date) => moment(date).format("DD MMM YYYY"),
      sorter: (a, b) => new Date(a.date_promised) - new Date(b.date_promised),
    },
    {
      title: "Reminder Date",
      dataIndex: "reminder_date",
      render: (date) => moment(date).format("DD MMM YYYY"),
      sorter: (a, b) => new Date(a.reminder_date) - new Date(b.reminder_date),
    },
    {
      title: "Status",
      render: (_, record) => {
        const status = moment(record.date_promised).isAfter(moment())
          ? "Pending"
          : "Overdue";
        return (
          <Tag
            color={statusColors[status]}
            icon={
              status === "Pending" ? (
                <ScheduleOutlined />
              ) : (
                <ExclamationCircleOutlined />
              )
            }
          >
            {status}
          </Tag>
        );
      },
      filters: [
        { text: "Pending", value: "Pending" },
        { text: "Overdue", value: "Overdue" },
        { text: "Completed", value: "Completed" },
      ],
      onFilter: (value, record) => {
        if (value === "Completed")
          return record.amount_paid >= record.amount_promised;
        const status = moment(record.date_promised).isAfter(moment())
          ? "Pending"
          : "Overdue";
        return status === value;
      },
    },
    {
      title: "Reminder Message",
      dataIndex: "reminder_text",
      render: (text) => <Text type="secondary">{text}</Text>,
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/loan/${record.loan_id.id}`)}
          disabled={record.amount_paid >= record.amount_promised}
        >
          Manage Loan
        </Button>
      ),
    },
  ];

  const exportToCSV = () => {
    const csvContent = [
      [
        "Amount Promised",
        "Date Promised",
        "Reminder Date",
        "Status",
        "Reminder Text",
      ],
      ...ptps.map((p) => [
        p.amount_promised,
        moment(p.date_promised).format("YYYY-MM-DD"),
        moment(p.reminder_date).format("YYYY-MM-DD"),
        moment(p.date_promised).isAfter(moment()) ? "Pending" : "Overdue",
        p.reminder_text,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ptps_${moment().format("YYYYMMDD")}.csv`;
    link.click();
  };

  const fetchPtps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/ptps?fields=*,loan_id.id,loan_id.borrower.first_name,loan_id.borrower.last_name",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              date_promised: {
                _between: [
                  filters.dateRange[0].format("YYYY-MM-DD"),
                  filters.dateRange[1].format("YYYY-MM-DD"),
                ],
              },
              company: company.id,
              ...(filters.status !== "all" && { status: filters.status }),
            },
            sort: "-date_promised",
          },
        }
      );
      setPtps(response.data.data);
    } catch (error) {
      console.error("Error fetching PTPs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (ptp) => {
    try {
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/ptps/${ptp.id}`,
        { status: "Completed" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPtps();
      message.success("PTP marked as completed!");
    } catch (error) {
      message.error("Failed to update PTP status");
    }
  };

  useEffect(() => {
    fetchPtps();
  }, [filters]);

  const getSummary = () => {
    const totalPtps = ptps.length;
    const totalPromised = ptps.reduce((sum, p) => sum + p.amount_promised, 0);
    const totalFulfilled = ptps.reduce(
      (sum, p) => sum + (p.amount_paid || 0),
      0
    );
    const overdueCount = ptps.filter(
      (p) =>
        moment(p.date_promised).isBefore(moment()) &&
        p.amount_paid < p.amount_promised
    ).length;

    return { totalPtps, totalPromised, totalFulfilled, overdueCount };
  };

  const summary = getSummary();

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3} style={{ marginBottom: 0 }}>
              Payment Promises (PTPs)
            </Title>
            <Text type="secondary">
              {company?.name} • Updated {moment().format("DD MMM YYYY")}
            </Text>
          </Col>
          <Col>
            <Button.Group>
              <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
                Export CSV
              </Button>
            </Button.Group>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Promises"
                value={summary.totalPtps}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Promised Amount"
                value={summary.totalPromised}
                valueStyle={{ color: "#3f8600" }}
                suffix="ZMW"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Fulfilled"
                value={summary.totalFulfilled}
                valueStyle={{ color: "#722ed1" }}
                suffix="ZMW"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Overdue Promises"
                value={summary.overdueCount}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: "100%" }}
            />
          </Col>
          {/* <Col span={12}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
            >
              <Select.Option value="all">All Statuses</Select.Option>
              <Select.Option value="Pending">Pending</Select.Option>
              <Select.Option value="Overdue">Overdue</Select.Option>
              <Select.Option value="Completed">Completed</Select.Option>
            </Select>
          </Col> */}
        </Row>

        {/* PTPs Table */}
        <Table
          columns={columns}
          dataSource={ptps}
          rowKey="id"
          loading={loading}
          scroll={{ x: true }}
          pagination={{ pageSize: 20 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total Outstanding</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong>
                    ZMW{" "}
                    {(
                      summary.totalPromised - summary.totalFulfilled
                    ).toLocaleString()}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    </div>
  );
};

export default PtpPage;
